import { Collapse } from '@mantine/core';
import { ChevronDown } from 'lucide-react';
import { FC, ReactElement, useMemo } from 'react';

import DroppableComponent from '@/components/DroppableComponent.tsx';
import PendingAppointmentCard from '@/components/room-view/AppointmentCard/PendingAppointmentCard.tsx';
import ResolvedAppointmentCard from '@/components/room-view/AppointmentCard/ResolvedAppointmentCard.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { ColumnType, KanbanColumn } from '@/models/RoomView.ts';
import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import { CheckInDataMap } from '@/models/call-system/dtos/user-appointment-dto.ts';

import { generateAppointmentKey, getInstanceId } from '@/utils/appointmentUtils.ts';
import { cn } from '@/utils/styleUtils.ts';

import DraggableComponent from '../DraggableComponent.tsx';

interface Props {
    column: KanbanColumn<ColumnType, DailyAppointment>;
    collapsed?: boolean;
    collapsable?: boolean;
    toggleCollapse?: (id: string) => void;
    checkInData: CheckInDataMap;
    onAppointmentClick: (appointment: DailyAppointment) => void;
    onMutateAppointments: () => void;
    isDragDisabled?: boolean;
    isDropDisabled?: boolean;
    nonDraggableAppointmentIds?: Set<string>;
}

const KanbanColumnComponent: FC<Props> = ({
    column,
    collapsed = false,
    collapsable = false,
    toggleCollapse,
    onAppointmentClick,
    checkInData,
    onMutateAppointments,
    isDragDisabled = false,
    isDropDisabled = false,
    nonDraggableAppointmentIds,
}) => {
    const sortAppointmentsByStartTime = useMemo(() => {
        return (appointments: DailyAppointment[], columnType: ColumnType): DailyAppointment[] =>
            [...appointments].sort((a, b) => {
                if (columnType === ColumnType.DISCHARGED) {
                    // For discharged column: sort in descending order (latest to earliest)
                    return new Date(b.start).getTime() - new Date(a.start).getTime();
                }
                // For all other columns: sort in ascending order (earliest to latest)
                return new Date(a.start).getTime() - new Date(b.start).getTime();
            });
    }, []);

    const buildAppointments = (): ReactElement[] => {
        const is_pending = column.type === ColumnType.EXPECTED || column.type === ColumnType.WAITING_ROOM;

        if (column.items.length === 0) return [];

        const sorted_appointments = sortAppointmentsByStartTime(column.items, column.type);

        return sorted_appointments.map((appointment) => {
            const check_in_data = checkInData?.get(generateAppointmentKey(getInstanceId(appointment), appointment.id));

            const card_component = is_pending ? (
                <PendingAppointmentCard
                    appointment={appointment}
                    onClick={onAppointmentClick}
                    checkInData={check_in_data}
                    onMutateAppointments={onMutateAppointments}
                />
            ) : (
                <ResolvedAppointmentCard
                    appointment={appointment}
                    onClick={onAppointmentClick}
                    checkInData={check_in_data}
                    onMutateAppointments={onMutateAppointments}
                    // isDischarged={column.type === ColumnType.DISCHARGED}
                />
            );

            return appointment.data.patient_name && !appointment.is_no_show ? (
                <DraggableComponent
                    id={appointment.id}
                    key={appointment.id}
                    disabled={isDragDisabled || nonDraggableAppointmentIds?.has(appointment.id)}
                >
                    {card_component}
                </DraggableComponent>
            ) : (
                <div key={appointment.id}>{card_component}</div>
            );
        });
    };

    const appointments = buildAppointments();

    return (
        <DroppableComponent id={column.id} disabled={isDropDisabled} className="m-2">
            <button
                className="mb-4 flex w-full justify-between px-2 tracking-wider text-slate-500"
                onClick={() => {
                    if (collapsable && toggleCollapse) {
                        toggleCollapse(column.id);
                    }
                }}
            >
                <Paragraph className="text-sm xl:text-base">
                    {`${column.title.toUpperCase()}${column.items.length > 0 ? ` (${column.items.length})` : ''}`}
                </Paragraph>
                {collapsable && (
                    <ChevronDown className={`text-slate-400 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
                )}
            </button>

            <Collapse expanded={!collapsed}>
                <div
                    className={cn(
                        'space-y-2 rounded-lg p-2',
                        column.items.length === 0 && 'bg-slate-50 dark:bg-neutral-700'
                    )}
                >
                    {column.items.length === 0 ? (
                        <Paragraph className="p-4 text-center text-slate-500 dark:text-neutral-300">
                            {column.type == ColumnType.TREATMENT_ROOM ? 'Patienten hier ziehen' : 'Keine Patienten'}
                        </Paragraph>
                    ) : (
                        appointments
                    )}
                </div>
            </Collapse>
        </DroppableComponent>
    );
};

export default KanbanColumnComponent;
